import {
     Alert,
     Badge,
     Button,
     Container,
     Group,
     NumberInput,
     Paper,
     Select,
     Stack,
     Text,
     Title,
     Center,
     Divider,
     Box,
     ThemeIcon,
     Grid,
     Card,
     List,
     Progress,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import {
     IconAlertCircle,
     IconCopy,
     IconCreditCard,
     IconCurrencyNaira,
     IconCurrencyDollar,
     IconCheck,
     IconArrowRight,
     IconWallet,
     IconBuildingBank,
     IconInfoCircle,
} from "@tabler/icons-react";
import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { HttpCodes } from "../../lib/data";
import { useAuth } from "../../contexts/AuthContext";
import { Endpoints } from "../../lib/endpoints";
import { usePerformRequest, PerformRequest } from "../../lib/usePerformRequest";
import { PaginatedResponse, NonPaginatedResponse } from "../../types";
import {
     ILoanRequestProps,
     IPaystackVirtualAccountDetails,
     ICardRepaymentInitResponse,
     IAvailableLoanPlansProps,
} from "../../types/Loan";
import Loader from "../../components/Loader";
import { useToastNotification } from "../../hooks/useToastNotification";

export default function LoanRepayment() {
     const { user, loading } = useAuth();
     const toast = useToastNotification();
     const [isInitiatingCardPayment, setIsInitiatingCardPayment] =
          useState(false);

     const { data: loanRequestsResponse } = usePerformRequest<
          PaginatedResponse<ILoanRequestProps[]>
     >({
          method: Endpoints.GetLoanHistory(1, 100).method,
          route: Endpoints.GetLoanHistory(1, 100).route,
     });
     // Calculate active loans (approved loans with outstanding balance)
     const activeLoans = useMemo(() => {
          if (!loanRequestsResponse?.data) return [];

          return loanRequestsResponse.data
               .filter((loan) => loan.status === "approved")
               .map((loan) => {
                    const loanProduct =
                         typeof loan.loanID === "object" ? loan.loanID : null;
                    if (!loanProduct) return null;

                    const principal = loan.amount;
                    const interestRate = loanProduct.interestRate || 0;
                    const duration = loan.duration;
                    const totalInterest =
                         (principal * interestRate * duration) / 100;
                    const totalDue = principal + totalInterest;
                    const amountPaid = loan.amountPaid || 0;
                    const outstandingBalance = Math.max(
                         0,
                         totalDue - amountPaid
                    );

                    return {
                         ...loan,
                         loanProduct,
                         principal,
                         totalInterest,
                         totalDue,
                         amountPaid,
                         outstandingBalance,
                         isFullyPaid: outstandingBalance <= 0,
                    };
               })
               .filter(
                    (loan): loan is NonNullable<typeof loan> =>
                         loan !== null && !loan.isFullyPaid
               );
     }, [loanRequestsResponse?.data]);

     const hasActiveLoans = activeLoans.length > 0;

     const approvedLoanOptions = useMemo(
          () =>
               activeLoans.map((loan) => ({
                    value: loan.id,
                    label: `${
                         typeof loan.loanProduct === "object"
                              ? loan.loanProduct.name
                              : "Loan"
                    } • Outstanding: ₦${loan.outstandingBalance.toLocaleString(
                         "en-NG"
                    )}`,
               })),
          [activeLoans]
     );

     const formatCurrency = (amount: number) => {
          return `₦${amount.toLocaleString("en-NG")}`;
     };

     const shouldFetchVirtualAccount = Boolean(user?.paystackCustomerCode);
     const repaymentAccountEndpoint = Endpoints.GetRepaymentVirtualAccount();
     const {
          data: virtualAccountResponse,
          isLoading: isLoadingVirtualAccount,
          status: virtualAccountStatus,
     } = usePerformRequest<
          NonPaginatedResponse<IPaystackVirtualAccountDetails>
     >({
          method: repaymentAccountEndpoint.method,
          route: shouldFetchVirtualAccount
               ? repaymentAccountEndpoint.route
               : "",
     });
     const virtualAccount = virtualAccountResponse?.data;
     const virtualAccountError =
          shouldFetchVirtualAccount &&
          typeof virtualAccountStatus === "number" &&
          virtualAccountStatus >= 400;

     const cardRepaymentForm = useForm({
          initialValues: {
               amount: 0,
               loanRequestId: "",
          },
          validate: {
               amount: (value) => {
                    if (!value || value <= 0) {
                         return "Amount must be greater than zero";
                    }
                    if (value < 1000) {
                         return "Minimum repayment amount is ₦1,000";
                    }
                    return null;
               },
          },
     });

     const handleCopy = async (value: string, label: string) => {
          if (!value) return;
          try {
               await navigator.clipboard.writeText(value);
               toast.success(`${label} copied to clipboard`);
          } catch (error) {
               toast.error(`Failed to copy ${label}`);
          }
     };

     const cardRepaymentEndpoint = Endpoints.InitiateCardRepayment();
     const handleInitiateCardRepayment = async (values: {
          amount: number;
          loanRequestId?: string;
     }) => {
          setIsInitiatingCardPayment(true);
          try {
               const response = await PerformRequest<
                    NonPaginatedResponse<ICardRepaymentInitResponse>
               >({
                    method: cardRepaymentEndpoint.method,
                    route: cardRepaymentEndpoint.route,
                    data: {
                         amount: values.amount,
                         loanRequestId: values.loanRequestId || undefined,
                    },
               });

               const repaymentInit = response?.data?.data;
               if (
                    response &&
                    response.status === HttpCodes.Success &&
                    repaymentInit?.authorization_url
               ) {
                    toast.success("Redirecting to Paystack...");
                    window.location.href = repaymentInit.authorization_url;
               } else {
                    toast.error(
                         response?.data?.message ||
                              response?.message ||
                              "Unable to initialize card repayment"
                    );
               }
          } catch (error: any) {
               toast.error(
                    error?.message ||
                         "An error occurred while initializing card repayment"
               );
          } finally {
               setIsInitiatingCardPayment(false);
          }
     };

     if (loading) {
          return (
               <Container size="xl" py="xl">
                    <Paper
                         shadow="sm"
                         p="xl"
                         radius="sm"
                         style={{
                              background: "white",
                              border: "2px solid #e5e7eb",
                         }}
                    >
                         <Stack align="center" gap="lg" py="xl">
                              <Loader size="xl" />
                              <Text size="lg" c="dimmed">
                                   Loading your profile...
                              </Text>
                         </Stack>
                    </Paper>
               </Container>
          );
     }

     return (
          <Container size="xl" py="xl">
               <Stack gap="xl">
                    <motion.div
                         initial={{ opacity: 0, y: -20 }}
                         animate={{ opacity: 1, y: 0 }}
                         transition={{ duration: 0.5 }}
                    >
                         <Paper
                              shadow="sm"
                              p="xl"
                              radius="lg"
                              style={{ border: "2px solid #e5e7eb" }}
                         >
                              <Stack gap="xl">
                                   <div>
                                        <Title
                                             order={2}
                                             fw={800}
                                             style={{ color: "#1F2937" }}
                                        >
                                             Loan Repayment
                                        </Title>
                                        <Text size="sm" c="dimmed">
                                             Repay your loans via your dedicated
                                             virtual account or Paystack card
                                             payments.
                                        </Text>
                                   </div>

                                   {/* Check if user has active loans */}
                                   {!hasActiveLoans ? (
                                        <motion.div
                                             initial={{
                                                  opacity: 0,
                                                  scale: 0.95,
                                             }}
                                             animate={{ opacity: 1, scale: 1 }}
                                             transition={{ duration: 0.3 }}
                                        >
                                             <Paper
                                                  p="xl"
                                                  radius="md"
                                                  style={{
                                                       background:
                                                            "linear-gradient(135deg, #DCFCE7 0%, #BBF7D0 100%)",
                                                       border: "2px solid #22C55E",
                                                  }}
                                             >
                                                  <Group
                                                       gap="md"
                                                       align="flex-start"
                                                  >
                                                       <ThemeIcon
                                                            size="xl"
                                                            radius="md"
                                                            style={{
                                                                 background:
                                                                      "#22C55E",
                                                            }}
                                                       >
                                                            <IconCheck
                                                                 size={28}
                                                            />
                                                       </ThemeIcon>
                                                       <div style={{ flex: 1 }}>
                                                            <Text
                                                                 size="lg"
                                                                 fw={700}
                                                                 style={{
                                                                      color: "#15803D",
                                                                 }}
                                                                 mb="xs"
                                                            >
                                                                 No Active Loans
                                                            </Text>
                                                            <Text
                                                                 size="sm"
                                                                 c="dimmed"
                                                            >
                                                                 You currently
                                                                 have no active
                                                                 loans requiring
                                                                 repayment. All
                                                                 your loans have
                                                                 been fully paid
                                                                 or you haven't
                                                                 been approved
                                                                 for any loans
                                                                 yet.
                                                            </Text>
                                                       </div>
                                                  </Group>
                                             </Paper>
                                        </motion.div>
                                   ) : (
                                        <>
                                             {/* Active Loans Details */}
                                             <motion.div
                                                  initial={{
                                                       opacity: 0,
                                                       y: 10,
                                                  }}
                                                  animate={{ opacity: 1, y: 0 }}
                                                  transition={{
                                                       duration: 0.3,
                                                       delay: 0.1,
                                                  }}
                                             >
                                                  <Paper
                                                       p="lg"
                                                       radius="md"
                                                       style={{
                                                            background:
                                                                 "linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 100%)",
                                                            border: "2px solid #3B82F6",
                                                       }}
                                                  >
                                                       <Group gap="sm" mb="md">
                                                            <ThemeIcon
                                                                 size="lg"
                                                                 radius="md"
                                                                 style={{
                                                                      background:
                                                                           "#3B82F6",
                                                                 }}
                                                            >
                                                                 <IconWallet
                                                                      size={24}
                                                                 />
                                                            </ThemeIcon>
                                                            <div>
                                                                 <Text
                                                                      size="md"
                                                                      fw={700}
                                                                      style={{
                                                                           color: "#3B82F6",
                                                                      }}
                                                                 >
                                                                      Active
                                                                      Loans
                                                                 </Text>
                                                                 <Text
                                                                      size="xs"
                                                                      c="dimmed"
                                                                 >
                                                                      {
                                                                           activeLoans.length
                                                                      }{" "}
                                                                      {activeLoans.length ===
                                                                      1
                                                                           ? "loan"
                                                                           : "loans"}{" "}
                                                                      requiring
                                                                      repayment
                                                                 </Text>
                                                            </div>
                                                       </Group>

                                                       <Stack gap="md">
                                                            {activeLoans.map(
                                                                 (
                                                                      loan,
                                                                      index
                                                                 ) => {
                                                                      const loanProduct =
                                                                           loan.loanProduct;
                                                                      const progress =
                                                                           loan.totalDue >
                                                                           0
                                                                                ? (loan.amountPaid /
                                                                                       loan.totalDue) *
                                                                                  100
                                                                                : 0;

                                                                      return (
                                                                           <Card
                                                                                key={
                                                                                     loan.id
                                                                                }
                                                                                p="md"
                                                                                radius="md"
                                                                                style={{
                                                                                     background:
                                                                                          "white",
                                                                                     border: "1px solid #e5e7eb",
                                                                                }}
                                                                           >
                                                                                <Stack gap="sm">
                                                                                     <Group
                                                                                          justify="space-between"
                                                                                          align="flex-start"
                                                                                     >
                                                                                          <div>
                                                                                               <Text
                                                                                                    size="sm"
                                                                                                    fw={
                                                                                                         600
                                                                                                    }
                                                                                               >
                                                                                                    {typeof loanProduct ===
                                                                                                    "object"
                                                                                                         ? loanProduct.name
                                                                                                         : "Loan"}
                                                                                               </Text>
                                                                                               <Text
                                                                                                    size="xs"
                                                                                                    c="dimmed"
                                                                                               >
                                                                                                    Loan
                                                                                                    ID:{" "}
                                                                                                    {loan.id.slice(
                                                                                                         -8
                                                                                                    )}
                                                                                               </Text>
                                                                                          </div>
                                                                                          <Badge
                                                                                               color={
                                                                                                    loan.outstandingBalance >
                                                                                                    0
                                                                                                         ? "orange"
                                                                                                         : "green"
                                                                                               }
                                                                                               size="lg"
                                                                                          >
                                                                                               {loan.outstandingBalance >
                                                                                               0
                                                                                                    ? "Active"
                                                                                                    : "Paid"}
                                                                                          </Badge>
                                                                                     </Group>

                                                                                     <Divider />

                                                                                     <Grid gutter="sm">
                                                                                          <Grid.Col
                                                                                               span={{
                                                                                                    base: 12,
                                                                                                    sm: 6,
                                                                                               }}
                                                                                          >
                                                                                               <Box>
                                                                                                    <Text
                                                                                                         size="xs"
                                                                                                         c="dimmed"
                                                                                                         mb={
                                                                                                              4
                                                                                                         }
                                                                                                    >
                                                                                                         Principal
                                                                                                         Amount
                                                                                                    </Text>
                                                                                                    <Text
                                                                                                         size="md"
                                                                                                         fw={
                                                                                                              600
                                                                                                         }
                                                                                                    >
                                                                                                         {formatCurrency(
                                                                                                              loan.principal
                                                                                                         )}
                                                                                                    </Text>
                                                                                               </Box>
                                                                                          </Grid.Col>
                                                                                          <Grid.Col
                                                                                               span={{
                                                                                                    base: 12,
                                                                                                    sm: 6,
                                                                                               }}
                                                                                          >
                                                                                               <Box>
                                                                                                    <Text
                                                                                                         size="xs"
                                                                                                         c="dimmed"
                                                                                                         mb={
                                                                                                              4
                                                                                                         }
                                                                                                    >
                                                                                                         Interest
                                                                                                         (
                                                                                                         {loanProduct?.interestRate ||
                                                                                                              0}
                                                                                                         %)
                                                                                                    </Text>
                                                                                                    <Text
                                                                                                         size="md"
                                                                                                         fw={
                                                                                                              600
                                                                                                         }
                                                                                                    >
                                                                                                         {formatCurrency(
                                                                                                              loan.totalInterest
                                                                                                         )}
                                                                                                    </Text>
                                                                                               </Box>
                                                                                          </Grid.Col>
                                                                                          <Grid.Col
                                                                                               span={{
                                                                                                    base: 12,
                                                                                                    sm: 6,
                                                                                               }}
                                                                                          >
                                                                                               <Box>
                                                                                                    <Text
                                                                                                         size="xs"
                                                                                                         c="dimmed"
                                                                                                         mb={
                                                                                                              4
                                                                                                         }
                                                                                                    >
                                                                                                         Total
                                                                                                         Amount
                                                                                                         Due
                                                                                                    </Text>
                                                                                                    <Text
                                                                                                         size="md"
                                                                                                         fw={
                                                                                                              700
                                                                                                         }
                                                                                                         style={{
                                                                                                              color: "#3B82F6",
                                                                                                         }}
                                                                                                    >
                                                                                                         {formatCurrency(
                                                                                                              loan.totalDue
                                                                                                         )}
                                                                                                    </Text>
                                                                                               </Box>
                                                                                          </Grid.Col>
                                                                                          <Grid.Col
                                                                                               span={{
                                                                                                    base: 12,
                                                                                                    sm: 6,
                                                                                               }}
                                                                                          >
                                                                                               <Box>
                                                                                                    <Text
                                                                                                         size="xs"
                                                                                                         c="dimmed"
                                                                                                         mb={
                                                                                                              4
                                                                                                         }
                                                                                                    >
                                                                                                         Amount
                                                                                                         Paid
                                                                                                    </Text>
                                                                                                    <Text
                                                                                                         size="md"
                                                                                                         fw={
                                                                                                              600
                                                                                                         }
                                                                                                         style={{
                                                                                                              color: "#22C55E",
                                                                                                         }}
                                                                                                    >
                                                                                                         {formatCurrency(
                                                                                                              loan.amountPaid
                                                                                                         )}
                                                                                                    </Text>
                                                                                               </Box>
                                                                                          </Grid.Col>
                                                                                          <Grid.Col
                                                                                               span={
                                                                                                    12
                                                                                               }
                                                                                          >
                                                                                               <Box>
                                                                                                    <Group
                                                                                                         justify="space-between"
                                                                                                         mb={
                                                                                                              4
                                                                                                         }
                                                                                                    >
                                                                                                         <Text
                                                                                                              size="xs"
                                                                                                              c="dimmed"
                                                                                                         >
                                                                                                              Outstanding
                                                                                                              Balance
                                                                                                         </Text>
                                                                                                         <Text
                                                                                                              size="sm"
                                                                                                              fw={
                                                                                                                   700
                                                                                                              }
                                                                                                              style={{
                                                                                                                   color: "#EF4444",
                                                                                                              }}
                                                                                                         >
                                                                                                              {formatCurrency(
                                                                                                                   loan.outstandingBalance
                                                                                                              )}
                                                                                                         </Text>
                                                                                                    </Group>
                                                                                                    <Progress
                                                                                                         value={
                                                                                                              progress
                                                                                                         }
                                                                                                         color={
                                                                                                              progress >=
                                                                                                              100
                                                                                                                   ? "green"
                                                                                                                   : "blue"
                                                                                                         }
                                                                                                         size="md"
                                                                                                         radius="sm"
                                                                                                    />
                                                                                                    <Text
                                                                                                         size="xs"
                                                                                                         c="dimmed"
                                                                                                         mt={
                                                                                                              4
                                                                                                         }
                                                                                                    >
                                                                                                         {progress.toFixed(
                                                                                                              1
                                                                                                         )}

                                                                                                         %
                                                                                                         repaid
                                                                                                    </Text>
                                                                                               </Box>
                                                                                          </Grid.Col>
                                                                                     </Grid>
                                                                                </Stack>
                                                                           </Card>
                                                                      );
                                                                 }
                                                            )}
                                                       </Stack>
                                                  </Paper>
                                             </motion.div>

                                             <Divider
                                                  label="Repayment Methods"
                                                  labelPosition="center"
                                             />

                                             {/* Repayment Methods */}
                                             <Stack gap="lg">
                                                  {/* Method 1: Virtual Account */}
                                                  <motion.div
                                                       initial={{
                                                            opacity: 0,
                                                            y: 10,
                                                       }}
                                                       animate={{
                                                            opacity: 1,
                                                            y: 0,
                                                       }}
                                                       transition={{
                                                            duration: 0.3,
                                                            delay: 0.2,
                                                       }}
                                                  >
                                                       <Paper
                                                            withBorder
                                                            p="lg"
                                                            radius="md"
                                                            style={{
                                                                 background:
                                                                      "linear-gradient(135deg, #F0FDF4 0%, #DCFCE7 100%)",
                                                                 border: "2px solid #22C55E",
                                                            }}
                                                       >
                                                            <Stack gap="md">
                                                                 <Group
                                                                      justify="space-between"
                                                                      align="flex-start"
                                                                 >
                                                                      <Group gap="sm">
                                                                           <ThemeIcon
                                                                                size="xl"
                                                                                radius="md"
                                                                                style={{
                                                                                     background:
                                                                                          "#22C55E",
                                                                                }}
                                                                           >
                                                                                <IconBuildingBank
                                                                                     size={
                                                                                          24
                                                                                     }
                                                                                />
                                                                           </ThemeIcon>
                                                                           <div>
                                                                                <Text
                                                                                     size="md"
                                                                                     fw={
                                                                                          700
                                                                                     }
                                                                                     style={{
                                                                                          color: "#15803D",
                                                                                     }}
                                                                                >
                                                                                     Pay
                                                                                     via
                                                                                     Virtual
                                                                                     Account
                                                                                </Text>
                                                                                <Text
                                                                                     size="sm"
                                                                                     c="dimmed"
                                                                                >
                                                                                     Transfer
                                                                                     funds
                                                                                     to
                                                                                     your
                                                                                     dedicated
                                                                                     account
                                                                                     -
                                                                                     payments
                                                                                     are
                                                                                     automatically
                                                                                     applied
                                                                                </Text>
                                                                           </div>
                                                                      </Group>
                                                                 </Group>

                                                                 {virtualAccount && (
                                                                      <Paper
                                                                           p="md"
                                                                           radius="md"
                                                                           style={{
                                                                                background:
                                                                                     "white",
                                                                                border: "1px solid #e5e7eb",
                                                                           }}
                                                                      >
                                                                           <Stack gap="sm">
                                                                                <Group justify="space-between">
                                                                                     <Text
                                                                                          size="sm"
                                                                                          c="dimmed"
                                                                                     >
                                                                                          Account
                                                                                          Number
                                                                                     </Text>
                                                                                     <Group gap="xs">
                                                                                          <Text
                                                                                               size="md"
                                                                                               fw={
                                                                                                    700
                                                                                               }
                                                                                               style={{
                                                                                                    fontFamily:
                                                                                                         "monospace",
                                                                                                    letterSpacing:
                                                                                                         "0.05em",
                                                                                               }}
                                                                                          >
                                                                                               {
                                                                                                    virtualAccount.accountNumber
                                                                                               }
                                                                                          </Text>
                                                                                          <Button
                                                                                               variant="light"
                                                                                               size="xs"
                                                                                               leftSection={
                                                                                                    <IconCopy
                                                                                                         size={
                                                                                                              14
                                                                                                         }
                                                                                                    />
                                                                                               }
                                                                                               onClick={() =>
                                                                                                    handleCopy(
                                                                                                         virtualAccount.accountNumber,
                                                                                                         "Account number"
                                                                                                    )
                                                                                               }
                                                                                          >
                                                                                               Copy
                                                                                          </Button>
                                                                                     </Group>
                                                                                </Group>
                                                                                <Group justify="space-between">
                                                                                     <Text
                                                                                          size="sm"
                                                                                          c="dimmed"
                                                                                     >
                                                                                          Account
                                                                                          Name
                                                                                     </Text>
                                                                                     <Text
                                                                                          size="sm"
                                                                                          fw={
                                                                                               600
                                                                                          }
                                                                                     >
                                                                                          {
                                                                                               virtualAccount.accountName
                                                                                          }
                                                                                     </Text>
                                                                                </Group>
                                                                                <Group justify="space-between">
                                                                                     <Text
                                                                                          size="sm"
                                                                                          c="dimmed"
                                                                                     >
                                                                                          Bank
                                                                                          Name
                                                                                     </Text>
                                                                                     <Text
                                                                                          size="sm"
                                                                                          fw={
                                                                                               600
                                                                                          }
                                                                                     >
                                                                                          {virtualAccount.bankName ||
                                                                                               "N/A"}
                                                                                     </Text>
                                                                                </Group>
                                                                           </Stack>
                                                                      </Paper>
                                                                 )}

                                                                 <Paper
                                                                      p="md"
                                                                      radius="md"
                                                                      style={{
                                                                           background:
                                                                                "#FFFBEB",
                                                                           border: "1px solid #FCD34D",
                                                                      }}
                                                                 >
                                                                      <Group
                                                                           gap="sm"
                                                                           mb="sm"
                                                                      >
                                                                           <ThemeIcon
                                                                                size="lg"
                                                                                radius="md"
                                                                                style={{
                                                                                     background:
                                                                                          "#F59E0B",
                                                                                }}
                                                                           >
                                                                                <IconInfoCircle
                                                                                     size={
                                                                                          20
                                                                                     }
                                                                                />
                                                                           </ThemeIcon>
                                                                           <Text
                                                                                size="sm"
                                                                                fw={
                                                                                     600
                                                                                }
                                                                                style={{
                                                                                     color: "#92400E",
                                                                                }}
                                                                           >
                                                                                How
                                                                                to
                                                                                Pay
                                                                                via
                                                                                Virtual
                                                                                Account
                                                                           </Text>
                                                                      </Group>
                                                                      <List
                                                                           size="sm"
                                                                           spacing="xs"
                                                                           icon={
                                                                                <ThemeIcon
                                                                                     size="sm"
                                                                                     radius="sm"
                                                                                     style={{
                                                                                          background:
                                                                                               "#22C55E",
                                                                                     }}
                                                                                >
                                                                                     <IconCheck
                                                                                          size={
                                                                                               14
                                                                                          }
                                                                                     />
                                                                                </ThemeIcon>
                                                                           }
                                                                      >
                                                                           <List.Item>
                                                                                <Text
                                                                                     size="sm"
                                                                                     c="dimmed"
                                                                                >
                                                                                     Copy
                                                                                     your
                                                                                     dedicated
                                                                                     virtual
                                                                                     account
                                                                                     number
                                                                                     above
                                                                                </Text>
                                                                           </List.Item>
                                                                           <List.Item>
                                                                                <Text
                                                                                     size="sm"
                                                                                     c="dimmed"
                                                                                >
                                                                                     Transfer
                                                                                     any
                                                                                     amount
                                                                                     from
                                                                                     your
                                                                                     bank
                                                                                     to
                                                                                     this
                                                                                     account
                                                                                </Text>
                                                                           </List.Item>
                                                                           <List.Item>
                                                                                <Text
                                                                                     size="sm"
                                                                                     c="dimmed"
                                                                                >
                                                                                     Payments
                                                                                     are
                                                                                     automatically
                                                                                     applied
                                                                                     to
                                                                                     your
                                                                                     active
                                                                                     loans
                                                                                </Text>
                                                                           </List.Item>
                                                                           <List.Item>
                                                                                <Text
                                                                                     size="sm"
                                                                                     c="dimmed"
                                                                                >
                                                                                     Oldest
                                                                                     loans
                                                                                     are
                                                                                     paid
                                                                                     first,
                                                                                     then
                                                                                     newest
                                                                                     loans
                                                                                </Text>
                                                                           </List.Item>
                                                                      </List>
                                                                 </Paper>

                                                                 {!shouldFetchVirtualAccount && (
                                                                      <Alert
                                                                           color="yellow"
                                                                           icon={
                                                                                <IconAlertCircle
                                                                                     size={
                                                                                          16
                                                                                     }
                                                                                />
                                                                           }
                                                                      >
                                                                           Verify
                                                                           your
                                                                           email
                                                                           to
                                                                           generate
                                                                           a
                                                                           Paystack
                                                                           customer
                                                                           profile
                                                                           and
                                                                           virtual
                                                                           account.
                                                                      </Alert>
                                                                 )}

                                                                 {shouldFetchVirtualAccount &&
                                                                      isLoadingVirtualAccount && (
                                                                           <Center py="sm">
                                                                                <Loader size="lg" />
                                                                           </Center>
                                                                      )}

                                                                 {virtualAccountError && (
                                                                      <Alert
                                                                           color="red"
                                                                           icon={
                                                                                <IconAlertCircle
                                                                                     size={
                                                                                          16
                                                                                     }
                                                                                />
                                                                           }
                                                                      >
                                                                           {virtualAccountResponse?.message ||
                                                                                "Unable to fetch your virtual account details. Please try again later."}
                                                                      </Alert>
                                                                 )}
                                                            </Stack>
                                                       </Paper>
                                                  </motion.div>

                                                  {/* Method 2: Card Payment */}
                                                  <motion.div
                                                       initial={{
                                                            opacity: 0,
                                                            y: 10,
                                                       }}
                                                       animate={{
                                                            opacity: 1,
                                                            y: 0,
                                                       }}
                                                       transition={{
                                                            duration: 0.3,
                                                            delay: 0.3,
                                                       }}
                                                  >
                                                       <Paper
                                                            withBorder
                                                            p="lg"
                                                            radius="md"
                                                            style={{
                                                                 background:
                                                                      "linear-gradient(135deg, #FDF2F8 0%, #FCE7F3 100%)",
                                                                 border: "2px solid #EC4899",
                                                            }}
                                                       >
                                                            <form
                                                                 onSubmit={cardRepaymentForm.onSubmit(
                                                                      handleInitiateCardRepayment
                                                                 )}
                                                            >
                                                                 <Stack gap="md">
                                                                      <Group gap="sm">
                                                                           <ThemeIcon
                                                                                size="xl"
                                                                                radius="md"
                                                                                style={{
                                                                                     background:
                                                                                          "#EC4899",
                                                                                }}
                                                                           >
                                                                                <IconCreditCard
                                                                                     size={
                                                                                          24
                                                                                     }
                                                                                />
                                                                           </ThemeIcon>
                                                                           <div>
                                                                                <Text
                                                                                     size="md"
                                                                                     fw={
                                                                                          700
                                                                                     }
                                                                                     style={{
                                                                                          color: "#BE185D",
                                                                                     }}
                                                                                >
                                                                                     Pay
                                                                                     with
                                                                                     Card
                                                                                </Text>
                                                                                <Text
                                                                                     size="sm"
                                                                                     c="dimmed"
                                                                                >
                                                                                     Enter
                                                                                     the
                                                                                     amount
                                                                                     and
                                                                                     pay
                                                                                     securely
                                                                                     via
                                                                                     Paystack
                                                                                </Text>
                                                                           </div>
                                                                      </Group>

                                                                      <NumberInput
                                                                           label="Repayment Amount (₦)"
                                                                           leftSection={
                                                                                <IconCurrencyNaira
                                                                                     size={
                                                                                          18
                                                                                     }
                                                                                />
                                                                           }
                                                                           hideControls
                                                                           min={
                                                                                1000
                                                                           }
                                                                           thousandSeparator=","
                                                                           size="md"
                                                                           placeholder="Enter amount to repay"
                                                                           styles={{
                                                                                input: {
                                                                                     fontSize:
                                                                                          "16px",
                                                                                     fontWeight: 500,
                                                                                },
                                                                           }}
                                                                           {...cardRepaymentForm.getInputProps(
                                                                                "amount"
                                                                           )}
                                                                      />

                                                                      {approvedLoanOptions.length >
                                                                           0 && (
                                                                           <Select
                                                                                label="Select Loan (Optional)"
                                                                                placeholder="Choose a specific loan to repay"
                                                                                description="If not selected, payment will be applied to oldest loan first"
                                                                                data={
                                                                                     approvedLoanOptions
                                                                                }
                                                                                value={
                                                                                     cardRepaymentForm
                                                                                          .values
                                                                                          .loanRequestId ||
                                                                                     null
                                                                                }
                                                                                onChange={(
                                                                                     value
                                                                                ) =>
                                                                                     cardRepaymentForm.setFieldValue(
                                                                                          "loanRequestId",
                                                                                          value ||
                                                                                               ""
                                                                                     )
                                                                                }
                                                                           />
                                                                      )}

                                                                      <Paper
                                                                           p="md"
                                                                           radius="md"
                                                                           style={{
                                                                                background:
                                                                                     "#FFFBEB",
                                                                                border: "1px solid #FCD34D",
                                                                           }}
                                                                      >
                                                                           <Group
                                                                                gap="sm"
                                                                                mb="sm"
                                                                           >
                                                                                <ThemeIcon
                                                                                     size="lg"
                                                                                     radius="md"
                                                                                     style={{
                                                                                          background:
                                                                                               "#F59E0B",
                                                                                     }}
                                                                                >
                                                                                     <IconInfoCircle
                                                                                          size={
                                                                                               20
                                                                                          }
                                                                                     />
                                                                                </ThemeIcon>
                                                                                <Text
                                                                                     size="sm"
                                                                                     fw={
                                                                                          600
                                                                                     }
                                                                                     style={{
                                                                                          color: "#92400E",
                                                                                     }}
                                                                                >
                                                                                     How
                                                                                     to
                                                                                     Pay
                                                                                     with
                                                                                     Card
                                                                                </Text>
                                                                           </Group>
                                                                           <List
                                                                                size="sm"
                                                                                spacing="xs"
                                                                                icon={
                                                                                     <ThemeIcon
                                                                                          size="sm"
                                                                                          radius="sm"
                                                                                          style={{
                                                                                               background:
                                                                                                    "#EC4899",
                                                                                          }}
                                                                                     >
                                                                                          <IconArrowRight
                                                                                               size={
                                                                                                    14
                                                                                               }
                                                                                          />
                                                                                     </ThemeIcon>
                                                                                }
                                                                           >
                                                                                <List.Item>
                                                                                     <Text
                                                                                          size="sm"
                                                                                          c="dimmed"
                                                                                     >
                                                                                          Enter
                                                                                          the
                                                                                          repayment
                                                                                          amount
                                                                                          (minimum
                                                                                          ₦1,000)
                                                                                     </Text>
                                                                                </List.Item>
                                                                                <List.Item>
                                                                                     <Text
                                                                                          size="sm"
                                                                                          c="dimmed"
                                                                                     >
                                                                                          Optionally
                                                                                          select
                                                                                          a
                                                                                          specific
                                                                                          loan
                                                                                          to
                                                                                          repay
                                                                                     </Text>
                                                                                </List.Item>
                                                                                <List.Item>
                                                                                     <Text
                                                                                          size="sm"
                                                                                          c="dimmed"
                                                                                     >
                                                                                          Click
                                                                                          "Pay
                                                                                          with
                                                                                          Card"
                                                                                          to
                                                                                          be
                                                                                          redirected
                                                                                          to
                                                                                          Paystack
                                                                                     </Text>
                                                                                </List.Item>
                                                                                <List.Item>
                                                                                     <Text
                                                                                          size="sm"
                                                                                          c="dimmed"
                                                                                     >
                                                                                          Complete
                                                                                          payment
                                                                                          with
                                                                                          your
                                                                                          card
                                                                                          details
                                                                                     </Text>
                                                                                </List.Item>
                                                                                <List.Item>
                                                                                     <Text
                                                                                          size="sm"
                                                                                          c="dimmed"
                                                                                     >
                                                                                          Payment
                                                                                          is
                                                                                          automatically
                                                                                          applied
                                                                                          to
                                                                                          your
                                                                                          loan
                                                                                     </Text>
                                                                                </List.Item>
                                                                           </List>
                                                                      </Paper>

                                                                      <Button
                                                                           type="submit"
                                                                           loading={
                                                                                isInitiatingCardPayment
                                                                           }
                                                                           leftSection={
                                                                                <IconCreditCard
                                                                                     size={
                                                                                          20
                                                                                     }
                                                                                />
                                                                           }
                                                                           size="md"
                                                                           fullWidth
                                                                           style={{
                                                                                background:
                                                                                     "linear-gradient(135deg, #EC4899 0%, #BE185D 100%)",
                                                                           }}
                                                                      >
                                                                           Pay
                                                                           with
                                                                           Card
                                                                      </Button>
                                                                 </Stack>
                                                            </form>
                                                       </Paper>
                                                  </motion.div>
                                             </Stack>
                                        </>
                                   )}
                              </Stack>
                         </Paper>
                    </motion.div>
               </Stack>
          </Container>
     );
}
